/**
 * ASSET DATA CONTEXT
 * 
 * Provides a React Context wrapper around the useAssets hook to share asset data
 * across multiple components without prop drilling. This context centralizes asset
 * data management and makes it available to any component in the component tree.
 * 
 * PURPOSE:
 * - Eliminates prop drilling for asset data across deep component hierarchies
 * - Provides a single source of truth for asset data state
 * - Enables multiple components to access the same asset data and loading states
 * - Centralizes asset fetching logic and error handling
 * 
 * USAGE PATTERN:
 * 1. Wrap components with AssetDataProvider at a high level
 * 2. Use useAssetData() hook in child components to access asset data
 * 3. All components automatically get updates when asset data changes
 */

import React, { createContext, useContext } from 'react';
import { useAssets } from '../hooks/useAssets';

// CONTEXT CREATION
// Create the context with null default value to enforce provider usage
const AssetDataContext = createContext(null);

/**
 * CUSTOM HOOK FOR CONSUMING ASSET DATA CONTEXT
 * 
 * Provides a safe way to access asset data context with built-in error checking.
 * Throws an error if used outside of AssetDataProvider to prevent runtime issues.
 * 
 * @returns {Object} Asset data object from useAssets hook containing:
 *   - data: Processed asset array with display names
 *   - isLoading: Loading state boolean
 *   - error: Error state if any
 *   - statusOptions, categoryOptions, locationsMap: Dropdown mappings
 *   - progress: Fetch progress information
 *   - cancelFetch, canCancel: Cancellation controls
 */
export const useAssetData = () => {
  const context = useContext(AssetDataContext);
  
  // SAFETY CHECK - Ensure hook is used within provider
  if (!context) {
    throw new Error('useAssetData must be used within an AssetDataProvider');
  }
  
  return context;
};

/**
 * ASSET DATA PROVIDER COMPONENT
 * 
 * Wraps the useAssets hook in a React Context Provider to share asset data
 * across multiple components. Acts as a bridge between the hook and context system.
 * 
 * PROPS:
 * @param {ReactNode} children - Child components that will have access to asset data
 * @param {string} selectedProjectId - ACC project ID for asset fetching
 * @param {string} selectedCategoryId - Category filter for assets
 * @param {string} assetFetchOptions - Query parameters for asset API calls
 * @param {*} reloadFlag - Flag to trigger data refetch when toggled
 * @param {Function} onProgress - Optional progress callback for fetch operations
 * @param {Function} getAccessToken - Authentication function for API calls
 * 
 * IMPLEMENTATION NOTES:
 * - Directly passes all props to useAssets hook
 * - Provides the entire useAssets return value through context
 * - No additional state management or data transformation
 * - Acts as a thin wrapper to enable context-based data sharing
 */
export const AssetDataProvider = ({
  children,
  selectedProjectId,
  selectedCategoryId,
  assetFetchOptions,
  reloadFlag,
  onProgress,
  getAccessToken
}) => {
  // HOOK INTEGRATION
  // Use the useAssets hook to get all asset-related data and functions
  // This includes processed data, loading states, options mappings, and controls
  const assetData = useAssets(
    selectedProjectId,
    selectedCategoryId,
    assetFetchOptions,
    reloadFlag,
    onProgress,
    getAccessToken
  );

  // CONTEXT PROVIDER
  // Make all asset data available to child components through React Context
  // Any component using useAssetData() will receive this complete object
  return (
    <AssetDataContext.Provider value={assetData}>
      {children}
    </AssetDataContext.Provider>
  );
};